import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';
import { deleteFile, postFile } from 'features/files/fileAPI';
import { FolderLocation, getStorageFolderName } from 'features/files/fileSlice';
import { ISiteState } from 'features/site/siteSlice';
import { toAsciiId } from 'helpers/helpers';
import {
  deleteMemberData,
  fetchMembers,
  IMember,
  patchMemberData,
  postMemberData,
} from './memberAPI';

export interface IMembersState {
  members: IMember[];
  status: 'idle' | 'loading' | 'failed';
  errors: any;
}

export type MembersStateProps = {
  members: IMember[];
};

export const initialState: IMembersState = {
  members: [],
  status: 'idle',
  errors: '',
};

export function getMemberAnchorId(member: IMember, useName = true) {
  return useName
    ? toAsciiId(member.firstname ?? '') || `member-${member.id}`
    : `member-${member.id}`;
}

export const fetchMembersAsync = createAsyncThunk(
  'members/fetchMembers',
  async () => {
    const res = await fetchMembers();
    return { response: res };
  },
);

export const postMemberAsync = createAsyncThunk(
  'members/postMember',
  async (
    {
      data,
    }: {
      data: IMember;
    },
    thunkAPI,
  ) => {
    try {
      return { response: await postMemberData(data) };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export const patchMemberAsync = createAsyncThunk(
  'members/patchMember',
  async (
    {
      data,
      file,
      member,
    }: {
      data: IMember;
      file?: File;
      member?: IMember;
    },
    thunkAPI,
  ) => {
    try {
      if (file || !data.image) {
        // we may be deleting the image
        // if there was an image, it needs to be deleted
        if (member?.image) {
          // 1.a from storage: TODO
          // 1.b from the db
          if (typeof member.image === 'object')
            await deleteFile(member.image.id);
          else await deleteFile(member.image);
        }
      }
      if (file) {
        // we have a new avatar
        // upload the new file
        const fd = await postFile(file, {
          description: 'Image for member ' + data.name,
          storage: getStorageFolderName(FolderLocation.Images),
          size: file.size,
          mimeType: file.type,
        });
        // keep the new file's id and uri
        data.image = { id: fd.data._id, uri: fd.data.message };
      }
      return { response: await patchMemberData(data) };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export const deleteMemberAsync = createAsyncThunk(
  'members/deleteMember',
  async (
    {
      id,
    }: {
      id: number;
    },
    thunkAPI,
  ) => {
    try {
      return { response: await deleteMemberData(id) };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ response: error });
    }
  },
);

export function addMemberCases(builder: ActionReducerMapBuilder<ISiteState>) {
  builder
    .addCase(fetchMembersAsync.pending, (state) => {
      state.status = 'loading';
    })
    .addCase(fetchMembersAsync.fulfilled, (state, action) => {
      state.status = 'idle';
      state.members = action.payload.response.data;
    })
    .addCase(fetchMembersAsync.rejected, (state, action) => {
      state.status = 'failed';
      state.errors = action.payload;
    })
    .addCase(postMemberAsync.pending, (state, action) => {
      state.status = 'loading';
      state.errors = action.payload;
    })
    .addCase(postMemberAsync.fulfilled, (state, action) => {
      state.status = 'idle';
      const id = action.payload?.response?.data?._id;
      if (id) {
        const data = { ...action.meta.arg.data, id };
        state.members.push(data);
      }
    })
    .addCase(postMemberAsync.rejected, (state, action) => {
      state.status = 'failed';
      state.errors = action.payload;
    })
    .addCase(patchMemberAsync.pending, (state, action) => {
      state.status = 'loading';
      state.errors = action.payload;
    })
    .addCase(patchMemberAsync.fulfilled, (state, action) => {
      state.status = 'idle';
      const m = state.members.find((m) => m.id === action.meta.arg.data.id);
      if (m) {
        Object.assign(m, action.meta.arg.data);
      }
    })
    .addCase(patchMemberAsync.rejected, (state, action) => {
      state.status = 'failed';
      state.errors = action.payload;
    })
    .addCase(deleteMemberAsync.pending, (state, action) => {
      state.status = 'loading';
      state.errors = action.payload;
    })
    .addCase(deleteMemberAsync.fulfilled, (state, action) => {
      state.status = 'idle';
      state.members = state.members.filter((m) => m.id !== action.meta.arg.id);
    })
    .addCase(deleteMemberAsync.rejected, (state, action) => {
      state.status = 'failed';
      state.errors = action.payload;
    });
}
